import { json2csv } from 'json-2-csv';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { Badge, Nav, Table } from 'react-bootstrap';
import {
  AdminBookingsTableQuery,
  BookingSpaceInvoiceLineType,
  Currency,
} from '../../../types/graphqlOperations';
import LoadingPage from '../../utilities/LoadingPage';
import PriceFormat from '../../utilities/PriceFormat';
import CancelledBookings from '../CancelledBookings';
import { DepartingGroups } from '../DepartingGroups';
import PeopleDepartingBookings from '../PeopleDepartingBookings';
import RecentBookings from '../RecentBookings';

export function bookingTableCSV(
  bookings: AdminBookingsTableQuery['bookings']['items'],
) {
  const prepareData = bookings.map((booking) => {
    const netCommission = booking.spaces.reduce((accumulator, sp) => {
      const spaceNetCommission = sp.invoice.reduce((acc, inv) => {
        if (inv.type !== BookingSpaceInvoiceLineType.TRANSACTION) {
          return acc + inv.guestAmount.amountZD - inv.hostAmount.amountZD;
        }

        return acc;
      }, 0);

      return accumulator + spaceNetCommission;
    }, 0);

    const activeSpaces = booking.spaces.filter((space) => space.active).length;

    const isCancelled = activeSpaces === 0;

    return {
      Created: DateTime.fromISO(booking.created).toLocaleString(
        DateTime.DATETIME_SHORT,
      ),
      User: `${booking.user.givenName} ${booking.user.familyName}`,
      Email: booking.user.email,
      Phone: booking.user.phone,
      Trip: booking.trip.title,
      TripSlug: booking.trip.slug,
      BookingID: booking.id,
      Start: DateTime.fromISO(booking.group.start.utc, {
        zone: booking.group.start.zone,
      }).toLocaleString(),
      NetCommission: netCommission,
      ActiveSpaces: activeSpaces,
      IsCancelled: isCancelled,
    };
  });

  return json2csv(prepareData, {});
}

/**
 * Bookings Table Row
 */
export function BookingRow({
  booking,
  isCancellation = false,
}: {
  booking: AdminBookingsTableQuery['bookings']['items'][0];
  isCancellation?: boolean;
}) {
  const netCommission = booking.spaces.reduce((accumulator, sp) => {
    const spaceNetCommission = sp.invoice.reduce((acc, inv) => {
      if (inv.type !== BookingSpaceInvoiceLineType.TRANSACTION) {
        return acc + inv.guestAmount.amountZD - inv.hostAmount.amountZD;
      }
      return acc;
    }, 0);
    return accumulator + spaceNetCommission;
  }, 0);

  const activeSpaces = booking.spaces.filter((space) => space.active).length;

  const gbpExchangeRate = parseFloat(booking.spaces[0].gbpExchangeRate);

  const cancellationDate = booking?.spaces
    ?.find((space) => space.active === false)
    ?.invoice.find(
      (inv) => inv.type === BookingSpaceInvoiceLineType.CANCELLATION,
    )?.created;

  const isCancelled = activeSpaces === 0;

  const cancellationStatus = useMemo(() => {
    if (activeSpaces === 0) {
      return 'Fully Cancelled';
    }
    if (activeSpaces < booking.spaces.length) {
      return 'Partially Cancelled';
    }
    return undefined;
  }, [activeSpaces, booking.spaces.length]);

  return (
    <tr className={isCancelled && !isCancellation ? 'table-light' : ''}>
      <td>
        {isCancellation ? (
          <>
            {DateTime.fromISO(cancellationDate).toLocaleString(
              DateTime.DATETIME_SHORT,
            )}

            <span className="text-muted px-2">
              (booked on{' '}
              {DateTime.fromISO(booking.created).toLocaleString(
                DateTime.DATETIME_SHORT,
              )}
              )
            </span>
          </>
        ) : (
          DateTime.fromISO(booking.created).toLocaleString(
            DateTime.DATETIME_SHORT,
          )
        )}
      </td>
      <td>
        <p>
          <strong>
            <Link href={`/me/${booking.user.id}`}>
              {`${booking.user.givenName} ${booking.user.familyName}`}
            </Link>
          </strong>
          <br />
          <Link href={`mailto:${booking.user.email}`}>
            {booking.user.email}
          </Link>
          <br />
          {booking.user.phone && (
            <Link href={`tel:${booking.user.phone}`}>{booking.user.phone}</Link>
          )}
        </p>
      </td>
      <td>
        <p>
          <strong>
            <Link href={`/trips/${booking.trip.slug}`}>
              {booking.trip.title}
            </Link>
          </strong>
          {!isCancelled && (
            <span className="text-muted"> (x{activeSpaces})</span>
          )}
          <br />
          <Link href={`/me/${booking.user.id}/bookings/${booking.id}`}>
            {booking.id}
            {cancellationStatus && (
              <Badge bg="warning" className="ms-1" text="dark">
                {cancellationStatus}
              </Badge>
            )}
          </Link>
        </p>
      </td>
      <td>
        {DateTime.fromISO(booking.group.start.utc, {
          zone: booking.group.start.zone,
        }).toLocaleString()}
      </td>
      {isCancellation ? null : (
        // hide net commission and cash received for cancellations
        <>
          <td>
            <PriceFormat
              amount={netCommission / gbpExchangeRate}
              currencyIso={'GBP'}
              short
              roundAmountOff
            />
            {booking.spaces![0].grossCommission.currency !== Currency.GBP && (
              <span className="text-muted text-sm px-1">
                (
                <PriceFormat
                  amount={netCommission}
                  currencyIso={booking.spaces![0].grossCommission.currency}
                  short
                  roundAmountOff
                />
                )
              </span>
            )}
          </td>

          <td>
            <PriceFormat
              amount={
                booking.commissionDetails.cashReceived.amountZD /
                gbpExchangeRate
              }
              currencyIso={'GBP'}
              short
              roundAmountOff
            />

            {booking.commissionDetails.cashReceived.currency !==
              Currency.GBP && (
              <span className="text-muted text-sm px-1">
                (
                <PriceFormat
                  amount={booking.commissionDetails.cashReceived.amountZD}
                  currencyIso={booking.commissionDetails.cashReceived.currency}
                  short
                  roundAmountOff
                />
                )
              </span>
            )}
          </td>
        </>
      )}
    </tr>
  );
}

/**
 * Bookings Table
 *
 * Creates the table for {@link AdminBookingsTable}
 */
export function BookingsTable({
  bookings,
  loading,
  isCancellation = false,
}: {
  // eslint-disable-next-line react/require-default-props
  bookings?: AdminBookingsTableQuery['bookings']['items'];
  loading: boolean;
  isCancellation?: boolean;
}) {
  // Handle loading
  if (loading && !bookings) return <LoadingPage />;

  return (
    <Table bordered>
      <thead>
        <tr>
          <th>{isCancellation ? 'Cancelled' : 'Created'}</th>
          <th>User</th>
          <th>Trip</th>
          <th>Start</th>
          {!isCancellation ? (
            // hide net commission and cash received for cancellations
            <>
              <th>Net Commission</th>
              <th>Cash Received</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {(bookings ?? []).map((booking) => (
          <BookingRow
            booking={booking}
            key={booking.id}
            isCancellation={isCancellation}
          />
        ))}
      </tbody>
    </Table>
  );
}

const tabName = 'bookingViewType';

type BookingViewType =
  | 'recent-bookings'
  | 'people-departing'
  | 'groups-departing'
  | 'cancellations';

/**
 * Admin Bookings Table
 *
 * Shows recent bookings or bookings due to depart in the next 2 months.
 */
export default function AdminBookingsTable() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const activeTab =
    (searchParams.get(tabName) as BookingViewType) ?? 'recent-bookings';

  const setActiveTab = useCallback(
    (tab: BookingViewType) => {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set(tabName, tab);
      router.push(`${pathname}?${newSearchParams.toString()}`);
    },
    [pathname, router, searchParams],
  );

  return (
    <>
      <Nav variant="tabs" className="my-2" defaultActiveKey={'recent-bookings'}>
        <Nav.Item key="recent-bookings">
          <Nav.Link
            active={activeTab === 'recent-bookings'}
            role="button"
            onClick={() => setActiveTab('recent-bookings')}
          >
            Recent Bookings
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'people-departing'}
            role="button"
            onClick={() => {
              setActiveTab('people-departing');
            }}
          >
            People Departing
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'groups-departing'}
            role="button"
            onClick={() => {
              setActiveTab('groups-departing');
            }}
          >
            Groups Departing
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link
            active={activeTab === 'cancellations'}
            role="button"
            onClick={() => {
              setActiveTab('cancellations');
            }}
          >
            Cancelled Bookings
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {activeTab === 'recent-bookings' && <RecentBookings />}
      {activeTab === 'people-departing' && <PeopleDepartingBookings />}
      {activeTab === 'groups-departing' && <DepartingGroups />}
      {activeTab === 'cancellations' && <CancelledBookings />}
    </>
  );
}
